import { useCallback } from 'react';
import useLoading from '@keyslabor/shared/hooks/useLoading';
import { toast } from 'sonner';
import axios, { stripHtml } from '@/api/axios.ts';
import {
  AxiosError,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';
import { getCsrfToken } from '@/api/utils/getCsrfToken.ts';
import { useNavigate } from 'react-router-dom';
import { Routes } from '@/globalTypes.ts';

interface Props<T> {
  url?: string;
  type?: 'POST' | 'GET' | 'DELETE' | 'UPDATE' | 'PUT';
  onEnd?(): void;
  onSuccess?(data: T): void;
  onError?(error: unknown): void;
  passthroughError?: boolean;
  successMessageOverride?: string;
}

interface CallbackProps {
  fields?: Record<string, string | string[] | number | number[] | boolean>;
  url?: string;
}

type DetailedErrorResponse = {
  detail?: string;
  errors?: Record<string, unknown>;
  [key: string]: unknown;
};

function isDetailedErrorResponse(
  error: AxiosError
): error is AxiosError<DetailedErrorResponse> {
  return (
    typeof error.response?.data === 'object' && error.response?.data !== null
  );
}

function getToastId(error: unknown) {
  if (isAxiosError(error) && error.status === 401) {
    return 'unauthorizedError';
  }
  return undefined;
}

function flattenValidationMessages(value: unknown): string[] {
  if (typeof value === 'string') {
    const trimmed = stripHtml(value).trim();
    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(flattenValidationMessages);
  }

  if (typeof value === 'object' && value !== null) {
    return Object.values(value).flatMap(flattenValidationMessages);
  }

  return [];
}

function resolveErrorMessage(error: AxiosError): string {
  if (error.status === 401) {
    return 'You are not authorized to perform this action.';
  }

  if (isDetailedErrorResponse(error)) {
    const data = error.response?.data;

    if (!data) {
      return error.message;
    }

    if (typeof data.detail === 'string') {
      return stripHtml(data.detail) || error.message;
    }

    if (data.errors) {
      const messages = flattenValidationMessages(data.errors);
      if (messages.length > 0) {
        return messages.join(', ');
      }
    }

    const fallbackMessages = flattenValidationMessages(data);
    if (fallbackMessages.length > 0) {
      return fallbackMessages.join(', ');
    }
  }

  return error.message;
}

function getErrorMessage(error: unknown) {
  const fallback = 'Something went wrong, please try again later';

  if (isAxiosError(error)) {
    return resolveErrorMessage(error);
  }

  if (error instanceof Error) {
    return stripHtml(error.message) || fallback;
  }

  if (isErrorWithResponse(error)) {
    if (typeof error.responseJSON.detail === 'string') {
      return error.responseJSON.detail;
    }

    if (Object.values(error.responseJSON).length > 0) {
      return Object.values(error.responseJSON).join(', ');
    }

    return fallback;
  }

  return fallback;
}

type ErrorWithResponse = {
  responseJSON: {
    detail: string | unknown;
    [key: string]: unknown;
  };
};

const isErrorWithResponse = (error: unknown): error is ErrorWithResponse => {
  return typeof (error as ErrorWithResponse)?.responseJSON === 'object';
};

const prepareFormData = (
  methodType: string,
  fields?: Record<string, string | string[] | number | number[] | boolean>
) => {
  if (['GET', 'HEAD'].includes(methodType.toUpperCase())) {
    return;
  }

  const formData = new FormData();

  if (methodType.toUpperCase() === 'POST') {
    formData.append('csrfmiddlewaretoken', getCsrfToken());
  }

  if (!fields) return formData;

  for (const [key, val] of Object.entries(fields)) {
    if (Array.isArray(val)) {
      for (const el of val) {
        formData.append(key, el.toString());
      }
      continue;
    }
    formData.append(key, val.toString());
  }
  return formData;
};

export default function useAjax<T>(props?: Props<T>) {
  const {
    url,
    onEnd,
    onError,
    onSuccess,
    type = 'POST',
    passthroughError = true,
    successMessageOverride,
  } = props ?? {};

  const { loading, onFinishLoading, onStartLoading } = useLoading();
  const navigate = useNavigate();

  const handleAjax = useCallback(
    async (callbackProps?: CallbackProps) => {
      const formData = prepareFormData(type, callbackProps?.fields);

      onStartLoading();

      try {
        const requestOptions: InternalAxiosRequestConfig = {
          method: type,
          url: url ?? callbackProps?.url,
          data: formData, // For GET requests, data should not be sent
          headers: {
            'Content-Type': 'multipart/form-data',
          } as AxiosRequestHeaders,
          withCredentials: true,
        };
        const response = await axios.request(requestOptions);
        // console.log(response);
        const data = response.data;
        if (data.success || successMessageOverride) {
          toast.success(successMessageOverride ?? data.success);
        }
        if (response.status === 401) {
          const unauthorisedError = AxiosError.from(
            new Error('You are not authorized to perform this action.'),
            'ERR_BAD_REQUEST',
            requestOptions,
            response.request
          );

          unauthorisedError.status = 401;
          throw unauthorisedError;
        }
        onSuccess?.(data);
        onFinishLoading?.();
        onEnd?.();
        return data;
      } catch (error) {
        onError?.(error);

        const errorMessage = getErrorMessage(error);

        toast.error(errorMessage, {
          id: getToastId(error),
        });

        if (isAxiosError(error) && error.status === 401) {
          navigate(Routes.Login); // Redirect to login page
        } else if (passthroughError) {
          throw error;
        }
      } finally {
        onFinishLoading?.();
      }
      return {} as T;
    },
    [url, type]
  );

  return [handleAjax, loading] as const;
}
